import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LayoutList, MessageSquare, Vote, Star, Info, ChevronRight, Loader2, Mic, MapPin, Calendar, Radio } from "lucide-react";
import api from "@/lib/apiClient";
import { getDeviceId } from "@/lib/device";
import { LogoMark } from "@/components/brand/Logo";
import { QuestionsPanel, PollsPanel, FeedbackPanel, SurveyPanel } from "@/components/attendee/panels";

const tabs = [
  { key: "sessions", label: "Sessions", icon: LayoutList },
  { key: "questions", label: "Q&A", icon: MessageSquare },
  { key: "polls", label: "Polls", icon: Vote },
  { key: "feedback", label: "Feedback", icon: Star },
  { key: "info", label: "Info", icon: Info },
];

export default function AttendeeEvent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sessions");

  useEffect(() => {
    const dev = getDeviceId();
    api.get(`/attendee/event/${eventId}`).then(({ data }) => setData(data)).catch(() => setData(false));
    const key = `joined_${eventId}`;
    if (!localStorage.getItem(key)) { api.post(`/attendee/event/${eventId}/join`, { device_id: dev }).catch(() => {}); localStorage.setItem(key, "1"); }
  }, [eventId]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center bg-muted/30"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center bg-muted/30 p-8 text-center text-muted-foreground">Event not found.</div>;

  const { event, sessions, current_session_id } = data;
  const current = sessions.find((s) => s.id === current_session_id);
  const others = sessions.filter((s) => s.id !== current_session_id);
  const goToSession = (sid) => navigate(`/attendee/${eventId}/session/${sid}`);

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col max-w-md mx-auto">
      <header className="bg-primary text-primary-foreground px-4 pt-5 pb-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          {event.logo ? <img src={event.logo} alt="" className="h-6 w-6 rounded" /> : <LogoMark className="h-6 w-6" />}
          <span className="font-display font-bold text-sm">Pitso<span className="text-accent">IO</span></span>
        </div>
        <p className="text-[10px] uppercase tracking-[0.16em] text-accent font-bold">{event.status === "live" ? "● Live now" : event.status}</p>
        <h1 className="font-display text-xl font-bold leading-tight mt-0.5">{event.name}</h1>
        <p className="text-xs text-white/60 mt-0.5">{event.venue || event.location}</p>
      </header>

      <main className="flex-1 px-4 py-4 pb-24">
        {tab === "sessions" && (
          <div className="space-y-4" data-testid="attendee-sessions">
            {current && (
              <div className="rounded-2xl bg-card border-2 border-accent p-5 shadow-sm" data-testid="attendee-current-session">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-accent"><Radio className="h-3.5 w-3.5" /> HAPPENING NOW</span>
                <h2 className="font-display text-2xl font-bold text-foreground leading-tight mt-1">{current.title}</h2>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1"><Mic className="h-3.5 w-3.5" /> {current.speaker || "TBA"}{current.room ? ` · ${current.room}` : ""}</p>
                <button onClick={() => goToSession(current.id)} data-testid="attendee-enter-qa"
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground font-semibold text-base h-14 active:scale-[0.99] transition-transform">
                  <MessageSquare className="h-5 w-5" /> Ask a Question
                </button>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => goToSession(current.id)} className="rounded-xl border border-border py-3 text-sm font-medium text-foreground active:bg-muted transition-colors">Polls</button>
                  <button onClick={() => goToSession(current.id)} className="rounded-xl border border-border py-3 text-sm font-medium text-foreground active:bg-muted transition-colors">Feedback</button>
                </div>
              </div>
            )}

            {others.length > 0 && <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">{current ? "Other sessions" : "Sessions"}</p>}
            {sessions.length === 0 ? <p className="text-center py-10 text-sm text-muted-foreground">No sessions listed yet.</p> : others.map((s) => (
              <button key={s.id} onClick={() => goToSession(s.id)} data-testid={`attendee-session-${s.id}`}
                className="w-full flex items-center justify-between rounded-xl bg-card border border-border p-4 text-left active:scale-[0.99] transition-transform">
                <div>
                  <p className="font-medium text-foreground">{s.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Mic className="h-3 w-3" /> {s.speaker || "TBA"}{s.room ? ` · ${s.room}` : ""}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
        {tab === "questions" && <QuestionsPanel eventId={eventId} sessionId={null} />}
        {tab === "polls" && <PollsPanel eventId={eventId} sessionId={null} />}
        {tab === "feedback" && <FeedbackPanel eventId={eventId} sessionId={null} />}
        {tab === "info" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-card border border-border p-4">
              <h3 className="font-display font-bold text-foreground mb-2">About</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{event.description || "No description."}</p>
              <div className="mt-3 space-y-2 text-sm">
                {event.date && <p className="flex items-center gap-2 text-muted-foreground"><Calendar className="h-4 w-4 text-accent" /> {event.date}</p>}
                {(event.venue || event.location) && <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4 text-accent" /> {event.venue || event.location}</p>}
              </div>
            </div>
            <div><h3 className="font-display font-bold text-foreground mb-2">Surveys</h3><SurveyPanel eventId={eventId} /></div>
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 inset-x-0 max-w-md mx-auto bg-card border-t border-border grid grid-cols-5 z-20">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`attendee-tab-${t.key}`}
            className={`flex flex-col items-center gap-1 py-3 transition-colors ${tab === t.key ? "text-accent" : "text-muted-foreground"}`}>
            <t.icon className="h-5 w-5" /><span className="text-[10px] font-medium">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
